import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDragDrop } from '../hooks/useDragDrop';
import { DeferredBlueprintUpload, uploadDrawing } from '../api/blueprintUploadApi';
import { CropFraction, FULL_CROP, HEIC_REJECTED_MESSAGE, isFullCrop, isHeicFile } from '../utils/pdf/blueprintSourceLogic';
import BlueprintCropStep from './BlueprintCropStep';

export type { DeferredBlueprintUpload } from '../api/blueprintUploadApi';

/** A picked PDF: its bytes (kept for page extraction) and the page-picker thumbnails. */
interface PickedPdf {
    bytes: ArrayBuffer;
    thumbnails: string[];
}

/** The drawing shown in the crop step: the image file itself, or the chosen PDF page rendered. */
interface CropPreview {
    url: string;
    /** Page the preview shows (PDF); null for an image. */
    pageIndex: number | null;
}

const readFileBytes = (file: File): Promise<ArrayBuffer> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = () => reject(new Error('Filen kunde inte läsas'));
        reader.readAsArrayBuffer(file);
    });

interface BlueprintUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Called after the pointer is written. Not called when `deferPointer` is set. */
    onSuccess?: (newBlueprintKey: string) => void;
    /**
     * Upload the file and write the drawing node, but leave `currentBlueprintKey`
     * (and any `extraPointerUpdates`) unwritten; hand them to `onDeferredUpload`
     * so the page can switch drawings later in one commit (marker relocation).
     */
    deferPointer?: boolean;
    onDeferredUpload?: (result: DeferredBlueprintUpload) => void;
    buildingKey: string;
    /** Level whose blueprint is uploaded. Omit when `target` is given. */
    levelKey?: string;
    authorUid: string;
    initialFile?: File | null;
    /**
     * Alternative target for drawings that do not belong to a level (parking
     * areas). DB path == Storage path for the drawings map, plus the DB path of
     * the pointer that should be set to the new key.
     */
    target?: {
        blueprintsPath: string;
        currentBlueprintKeyPath: string;
        /**
         * Root-relative DB paths written in the same multi-path update as the
         * pointer (e.g. a parking area's `blueprintSource: 'own'`).
         */
        extraPointerUpdates?: Record<string, unknown>;
    };
    /**
     * Runs before any write; throw to abort (the message is shown in the modal).
     * Lets API-layer guards (e.g. admin verification) apply to this component-level upload.
     */
    beforeUpload?: () => Promise<void>;
}

const BlueprintUploadModal: React.FC<BlueprintUploadModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    buildingKey,
    levelKey,
    authorUid,
    initialFile,
    target,
    beforeUpload,
    deferPointer = false,
    onDeferredUpload
}) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [pickedPdf, setPickedPdf] = useState<PickedPdf | null>(null);
    const [selectedPageIndex, setSelectedPageIndex] = useState<number>(0);
    const [isProcessingPdf, setIsProcessingPdf] = useState(false);
    // Two steps: 'select' (file and, for a PDF with several pages, the page) and 'crop'.
    const [step, setStep] = useState<'select' | 'crop'>('select');
    const [preview, setPreview] = useState<CropPreview | null>(null);
    const [crop, setCrop] = useState<CropFraction>(FULL_CROP);
    const [isPreparingPreview, setIsPreparingPreview] = useState(false);
    // Guards against a slow preview render landing after another file or page was chosen
    const previewRequest = useRef(0);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadStep, setUploadStep] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!preview) return undefined;
        return () => URL.revokeObjectURL(preview.url);
    }, [preview]);

    const resetSelection = useCallback(() => {
        previewRequest.current++;
        setSelectedFile(null);
        setPickedPdf(null);
        setSelectedPageIndex(0);
        setStep('select');
        setPreview(null);
        setCrop(FULL_CROP);
        setIsPreparingPreview(false);
    }, []);

    // Opens the crop step with the whole drawing selected. A PDF page is rendered for it.
    const showCropStep = useCallback(async (file: File, pdfBytes: ArrayBuffer | null, pageIndex: number) => {
        const request = ++previewRequest.current;
        setError(null);
        setCrop(FULL_CROP);
        if (!pdfBytes) {
            setPreview({ url: URL.createObjectURL(file), pageIndex: null });
            setStep('crop');
            return;
        }
        setIsPreparingPreview(true);
        try {
            const { renderPdfPagePreview } = await import('../utils/pdf/blueprintSource');
            const rendered = await renderPdfPagePreview(pdfBytes, pageIndex + 1);
            if (request !== previewRequest.current) return;
            setPreview({ url: URL.createObjectURL(rendered.blob), pageIndex });
            setStep('crop');
        } catch (err) {
            if (request !== previewRequest.current) return;
            setError('Fel vid bearbetning av PDF-fil: ' + (err instanceof Error ? err.message : 'Okänt fel'));
        } finally {
            if (request === previewRequest.current) {
                setIsPreparingPreview(false);
            }
        }
    }, []);

    // One entry point for the file input, drag-drop and initialFile
    const acceptFile = useCallback(async (file: File | undefined) => {
        if (!file) return;

        setError(null);
        resetSelection();

        const isImage = file.type.startsWith('image/');
        const isPDF = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

        if (!isImage && !isPDF) {
            setError('Endast bilder (PNG, JPG, JPEG) och PDF-filer är tillåtna');
            return;
        }
        if (isImage && isHeicFile(file)) {
            setError(HEIC_REJECTED_MESSAGE);
            return;
        }

        if (isPDF) {
            setIsProcessingPdf(true);
            try {
                const bytes = await readFileBytes(file);
                const { renderPdfPageThumbnails } = await import('../utils/pdf/blueprintSource');
                const { thumbnails } = await renderPdfPageThumbnails(bytes);
                if (thumbnails.length === 0) {
                    throw new Error('PDF-filen innehåller inga sidor');
                }
                setPickedPdf({ bytes, thumbnails });
                setSelectedFile(file);
                // Nothing to pick on a single page: straight to the crop
                if (thumbnails.length === 1) {
                    await showCropStep(file, bytes, 0);
                }
            } catch (err) {
                setError('Fel vid bearbetning av PDF-fil: ' + (err instanceof Error ? err.message : 'Okänt fel'));
            } finally {
                setIsProcessingPdf(false);
            }
        } else {
            setSelectedFile(file);
            await showCropStep(file, null, 0);
        }
    }, [resetSelection, showCropStep]);

    const handleFilesDropped = useCallback((files: File[]) => {
        void acceptFile(files[0]);
    }, [acceptFile]);

    const { isDragOver, dragHandlers } = useDragDrop({ onFilesDropped: handleFilesDropped });

    // Process initial file when provided (e.g., from external drag-drop)
    useEffect(() => {
        if (isOpen && initialFile) {
            void acceptFile(initialFile);
        }
    }, [isOpen, initialFile, acceptFile]);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        void acceptFile(event.target.files?.[0]);
    };

    const goToCrop = () => {
        if (!selectedFile) return;
        // Back and forth without changing the page keeps the rectangle
        if (preview && preview.pageIndex === (pickedPdf ? selectedPageIndex : null)) {
            setStep('crop');
            return;
        }
        void showCropStep(selectedFile, pickedPdf?.bytes ?? null, selectedPageIndex);
    };

    const uploadBlueprint = async () => {
        if (!selectedFile) return;

        setIsUploading(true);
        setError(null);

        try {
            if (beforeUpload) {
                await beforeUpload();
            }

            // Resolve where the drawing lives: a level (default) or a custom target
            if (!target && !levelKey) {
                throw new Error('Ingen våning eller mål angiven för ritningen');
            }
            const uploadTarget = target ?? {
                blueprintsPath: `buildings/${buildingKey}/structure/levels/${levelKey}/blueprints`,
                currentBlueprintKeyPath: `buildings/${buildingKey}/structure/levels/${levelKey}/currentBlueprintKey`,
            };

            // Both files of the drawing: the PDF source and the derived raster (workspace CLAUDE.md, drawings)
            setUploadStep('Förbereder ritningen...');
            const { prepareDrawingFromPdf, prepareDrawingFromImage } = await import('../utils/pdf/blueprintSource');
            // An untouched rectangle uploads the whole drawing, exactly as before the crop step existed
            const appliedCrop = isFullCrop(crop) ? undefined : crop;
            const prepared = pickedPdf
                ? await prepareDrawingFromPdf(pickedPdf.bytes, selectedPageIndex + 1, selectedFile.name, appliedCrop)
                : await prepareDrawingFromImage(selectedFile, appliedCrop);

            setUploadStep('Laddar upp...');
            const result = await uploadDrawing(prepared, uploadTarget, authorUid, deferPointer);

            // Deferred: the page decides when (and whether) the drawing becomes current.
            if (deferPointer) {
                onDeferredUpload?.(result);
            } else {
                onSuccess?.(result.key);
            }
            resetSelection();
            onClose();
        } catch (err) {
            console.error('Error uploading blueprint:', err);
            setError(err instanceof Error ? err.message : 'Ett fel uppstod vid uppladdning av ritning');
        } finally {
            setIsUploading(false);
            setUploadStep(null);
        }
    };

    const handleClose = () => {
        resetSelection();
        setError(null);
        setIsProcessingPdf(false);
        onClose();
    };

    const isBusy = isUploading || isProcessingPdf || isPreparingPreview;
    const canContinue = Boolean(selectedFile) && !isBusy;
    const canUpload = canContinue && step === 'crop' && preview !== null;

    if (!isOpen) return null;

    return (
        <>
            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>
            <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
        }}>
            <div
                {...dragHandlers}
                style={{
                    backgroundColor: isDragOver ? '#f0f8ff' : 'white',
                    padding: '30px',
                    borderRadius: '10px',
                    maxWidth: step === 'crop' ? '900px' : '500px',
                    width: '90%',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    border: isDragOver ? '2px dashed #007bff' : '2px solid transparent',
                    transition: 'all 0.2s ease'
                }}
            >
                <h3 style={{ marginTop: 0, marginBottom: '20px' }}>
                    {step === 'crop' ? 'Beskär ritningen' : 'Ladda upp ny ritning'}
                </h3>

                {step === 'crop' && preview && (
                    <div style={{ marginBottom: '20px' }}>
                        <BlueprintCropStep
                            previewUrl={preview.url}
                            crop={crop}
                            onChange={setCrop}
                            showPixelSize={!pickedPdf}
                            disabled={isUploading}
                        />
                    </div>
                )}

                {step === 'select' && (
                <>
                <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="blueprint-upload-file" style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                        Välj ritning (PNG, JPG, JPEG eller PDF):
                    </label>
                    <input
                        ref={fileInputRef}
                        id="blueprint-upload-file"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={handleFileSelect}
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ddd',
                            borderRadius: '5px'
                        }}
                    />
                    <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                        Ritningen sparas som PDF och visas skarpt i alla zoomnivåer. För en PDF med flera sidor väljer du vilken sida som ska användas. I nästa steg kan du beskära ritningen.
                    </small>
                </div>

                {isProcessingPdf && (
                    <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '5px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ 
                                width: '20px', 
                                height: '20px', 
                                border: '2px solid #2196f3', 
                                borderTop: '2px solid transparent', 
                                borderRadius: '50%', 
                                animation: 'spin 1s linear infinite' 
                            }}></div>
                            Bearbetar PDF-fil...
                        </div>
                    </div>
                )}

                {selectedFile && !isProcessingPdf && (
                    <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                        <strong>Vald fil:</strong> {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </div>
                )}

                {pickedPdf && (
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                            Välj sida att använda som ritning:
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                            {pickedPdf.thumbnails.map((page, index) => (
                                <div
                                    key={index}
                                    onClick={() => setSelectedPageIndex(index)}
                                    style={{
                                        border: selectedPageIndex === index ? '3px solid #007bff' : '2px solid #ddd',
                                        borderRadius: '8px',
                                        padding: '10px',
                                        cursor: 'pointer',
                                        backgroundColor: selectedPageIndex === index ? '#e3f2fd' : 'white',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <img
                                        src={page}
                                        alt={`Sida ${index + 1}`}
                                        style={{
                                            width: '100%',
                                            height: '150px',
                                            objectFit: 'contain',
                                            borderRadius: '4px'
                                        }}
                                    />
                                    <div style={{ textAlign: 'center', marginTop: '8px', fontWeight: '500' }}>
                                        Sida {index + 1}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                </>
                )}

                {error && (
                    <div style={{ 
                        marginBottom: '20px', 
                        padding: '10px', 
                        backgroundColor: '#f8d7da', 
                        color: '#721c24', 
                        borderRadius: '5px',
                        border: '1px solid #f5c6cb'
                    }}>
                        {error}
                    </div>
                )}

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                        onClick={handleClose}
                        disabled={isUploading}
                        style={{
                            padding: '10px 20px',
                            border: '1px solid #ddd',
                            backgroundColor: 'white',
                            borderRadius: '5px',
                            cursor: isUploading ? 'not-allowed' : 'pointer'
                        }}
                    >
                        Avbryt
                    </button>
                    {step === 'crop' && (
                        <button
                            onClick={() => setStep('select')}
                            disabled={isUploading}
                            style={{
                                padding: '10px 20px',
                                border: '1px solid #ddd',
                                backgroundColor: 'white',
                                borderRadius: '5px',
                                cursor: isUploading ? 'not-allowed' : 'pointer'
                            }}
                        >
                            Tillbaka
                        </button>
                    )}
                    {step === 'select' ? (
                        <button
                            onClick={goToCrop}
                            disabled={!canContinue}
                            style={{
                                padding: '10px 20px',
                                border: 'none',
                                backgroundColor: canContinue ? '#007bff' : '#6c757d',
                                color: 'white',
                                borderRadius: '5px',
                                cursor: canContinue ? 'pointer' : 'not-allowed'
                            }}
                        >
                            {isProcessingPdf ? 'Bearbetar PDF...' : isPreparingPreview ? 'Förbereder...' : 'Nästa'}
                        </button>
                    ) : (
                        <button
                            onClick={uploadBlueprint}
                            disabled={!canUpload}
                            style={{
                                padding: '10px 20px',
                                border: 'none',
                                backgroundColor: canUpload ? '#007bff' : '#6c757d',
                                color: 'white',
                                borderRadius: '5px',
                                cursor: canUpload ? 'pointer' : 'not-allowed'
                            }}
                        >
                            {isUploading ? (uploadStep ?? 'Laddar upp...') : 'Ladda upp'}
                        </button>
                    )}
                </div>
            </div>
        </div>
        </>
    );
};

export default BlueprintUploadModal;
